import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage, type Messaging } from 'firebase/messaging';
import {
  getFirebaseVapidKey,
  getFirebaseWebConfig,
  isFirebasePushConfigured,
} from '../config/firebase';
import { deviceTokensApi } from '../services/api';
import {
  getNotificationPermission,
  isIosDevice,
  isNotificationGranted,
  isStandalonePwa,
  supportsWebPush,
} from '../utils/pwaEnvironment';

const FCM_TOKEN_KEY = 'vo2_pwa_fcm_token';
const PUSH_ENABLED_KEY = 'vo2_pwa_push_enabled';

let firebaseApp: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let messagingSwRegistration: ServiceWorkerRegistration | null = null;

function readStoredFcmToken(): string {
  try {
    return localStorage.getItem(FCM_TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeStoredFcmToken(token: string): void {
  try {
    if (token) localStorage.setItem(FCM_TOKEN_KEY, token);
    else localStorage.removeItem(FCM_TOKEN_KEY);
  } catch {
    /* quota */
  }
}

export function isPushPreferenceEnabled(): boolean {
  try {
    return localStorage.getItem(PUSH_ENABLED_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setPushPreferenceEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(PUSH_ENABLED_KEY, enabled ? '1' : '0');
  } catch {
    /* quota */
  }
}

export type PushAvailability =
  | 'unsupported'
  | 'not_configured'
  | 'ios_needs_install'
  | 'permission_denied'
  | 'ready';

export function getPushAvailability(): PushAvailability {
  if (!supportsWebPush()) return 'unsupported';
  if (!isFirebasePushConfigured()) return 'not_configured';
  if (isIosDevice() && !isStandalonePwa()) return 'ios_needs_install';
  if (getNotificationPermission() === 'denied') return 'permission_denied';
  return 'ready';
}

async function ensureFirebaseMessaging(): Promise<Messaging | null> {
  if (!(await isSupported())) return null;
  if (!isFirebasePushConfigured()) return null;

  if (!firebaseApp) {
    firebaseApp = initializeApp(getFirebaseWebConfig());
  }
  if (!messaging) {
    messaging = getMessaging(firebaseApp);
  }
  return messaging;
}

async function ensureMessagingServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  if (messagingSwRegistration) return messagingSwRegistration;

  const existing = await navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope');
  if (existing) {
    messagingSwRegistration = existing;
    return existing;
  }

  messagingSwRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: '/firebase-cloud-messaging-push-scope',
  });
  return messagingSwRegistration;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!supportsWebPush()) return 'denied';
  const current = getNotificationPermission();
  if (current === 'granted') return 'granted';
  if (current === 'denied') return 'denied';
  return Notification.requestPermission();
}

/**
 * FCM token when the user has already granted notification permission.
 * Never calls Notification.requestPermission() — browsers require a direct user gesture for that.
 */
export async function acquireFcmTokenIfPermitted(): Promise<string | null> {
  const availability = getPushAvailability();
  if (availability !== 'ready') return null;
  if (!isNotificationGranted()) return null;

  const msg = await ensureFirebaseMessaging();
  if (!msg) return null;

  const registration = await ensureMessagingServiceWorker();
  if (!registration) return null;

  const vapidKey = getFirebaseVapidKey();
  const token = await getToken(msg, { vapidKey, serviceWorkerRegistration: registration });
  if (!token) return null;

  writeStoredFcmToken(token);
  return token;
}

export async function registerDeviceTokenWithApi(token: string): Promise<void> {
  if (!token) return;
  await deviceTokensApi.register({
    token,
    platform: 'web',
  });
}

export async function unregisterDeviceTokenFromApi(token?: string): Promise<void> {
  const value = token ?? readStoredFcmToken();
  if (!value) return;
  try {
    await deviceTokensApi.unregister({ token: value });
  } catch {
    /* logged out or network */
  }
  writeStoredFcmToken('');
}

/** Register FCM token with backend when user is authenticated and permission is already granted. */
export async function syncPushTokenAfterAuth(): Promise<string | null> {
  if (!isPushPreferenceEnabled()) return null;
  if (!isNotificationGranted()) return null;

  try {
    const token = await acquireFcmTokenIfPermitted();
    if (!token) return null;
    await registerDeviceTokenWithApi(token);
    return token;
  } catch {
    return null;
  }
}

/** Disable push on this device and remove token from backend. */
export async function disableWebPush(): Promise<void> {
  setPushPreferenceEnabled(false);
  await unregisterDeviceTokenFromApi();
}

/** Enable push preference and register token when permission is already granted. */
export async function enableWebPush(): Promise<string | null> {
  setPushPreferenceEnabled(true);
  if (!isNotificationGranted()) return null;
  return syncPushTokenAfterAuth();
}

export function subscribeForegroundPush(handler: (payload: { title: string; body: string; data: Record<string, string> }) => void): () => void {
  let cancelled = false;
  let unsubscribe: (() => void) | undefined;

  void (async () => {
    const msg = await ensureFirebaseMessaging();
    if (!msg || cancelled) return;
    unsubscribe = onMessage(msg, (remote) => {
      const data = Object.fromEntries(
        Object.entries(remote.data ?? {}).map(([k, v]) => [k, String(v)]),
      );
      handler({
        title: remote.notification?.title ?? data.title ?? 'VO2Peaks',
        body: remote.notification?.body ?? data.body ?? '',
        data,
      });
    });
  })();

  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}

export async function showBrowserNotification(title: string, body: string, data?: Record<string, string>): Promise<void> {
  if (!isNotificationGranted()) return;
  const registration = messagingSwRegistration ?? (await navigator.serviceWorker.getRegistration('/firebase-cloud-messaging-push-scope'));
  const options: NotificationOptions = {
    body,
    icon: '/icons/icon-192.png',
    badge: '/icons/icon-192.png',
    data: data ?? {},
  };
  if (registration) {
    await registration.showNotification(title, options);
  } else if ('Notification' in window) {
    // eslint-disable-next-line no-new
    new Notification(title, options);
  }
}
